/**
 * The shared rule behind the range filters that read as "20+ min" or "31+ messages" rather than as a
 * pair of numbers, and how such a range is drawn on a slider.
 *
 * Call Duration and Message Count both store a bounded-below, open-above range, both draw it on
 * several surfaces, and both get it subtly wrong in the same ways. The surfaces are the filter
 * panel, the chip, the Smart Banner and the Conversations explorer drawer. Keeping one copy stops
 * the two
 * dimensions drifting into different answers for the same question. Each dimension wraps these with
 * its own ceiling and unit; see `callDurationSliderState.ts` and `messageCountSliderState.ts`.
 */

/** The value either bound of a range filter takes when it is not set. */
export const UNSET_RANGE_BOUND = 0;

/**
 * Whether a range runs as far as its filter can express, and so reads as "20+" rather than as a pair
 * of numbers.
 *
 * Two stored shapes mean this. The ordinary one is an upper bound sitting on the top of the track:
 * the ceiling is as high as the filter goes, so "20 to 200" is every value the filter can reach from
 * 20 and naming the ceiling adds nothing. The other is an absent upper bound, which a saved filter
 * can still carry and which means the same thing to a reader.
 *
 * Deliberately keyed to the full track and not to a restricted ceiling. A slider narrowed to a
 * source's own range stops at that source's own maximum, which is a real bound worth stating.
 */
export const isOpenEndedRange = (
    min: number | undefined,
    max: number | undefined,
    ceiling: number,
): boolean => {
    const lowerBound = min ?? UNSET_RANGE_BOUND;
    const upperBound = max ?? UNSET_RANGE_BOUND;

    return (
        lowerBound > UNSET_RANGE_BOUND &&
        (upperBound === UNSET_RANGE_BOUND || upperBound >= ceiling)
    );
};

/**
 * How a range should be drawn on a slider.
 *
 * Mostly the range itself. The exception is a range with no stored upper bound at all, which has no
 * literal position for its upper thumb. The thumb rests at the end of the track and the label
 * below carries the meaning. Drawing the raw pair instead puts the upper thumb at zero, to the
 * *left* of the lower one, which MUI then silently sorts into a range nobody asked for.
 *
 * The open-ended label is returned rather than left to AcxSlider, which only knows how to print a
 * range or the empty state.
 */
export const resolveOpenEndedSliderState = (
    range: number[] | undefined,
    options: { ceiling: number; maxBound?: number; unitLabel: string },
): { value: number[]; underSlideLabel?: string } => {
    const maxBound = options.maxBound ?? options.ceiling;
    const value = range ?? [UNSET_RANGE_BOUND, UNSET_RANGE_BOUND];
    const [selectedMin, selectedMax] = value;

    if (!isOpenEndedRange(selectedMin, selectedMax, options.ceiling)) {
        return { value };
    }

    return {
        value: [
            selectedMin,
            selectedMax === UNSET_RANGE_BOUND ? maxBound : selectedMax,
        ],
        underSlideLabel: `${selectedMin}+ ${options.unitLabel}`,
    };
};
